import React, { useState, useEffect } from 'react';
import apiClient from '../../api/api';
import AuthLayout from '../../components/AuthLayout/AuthLayout';

const ReportSummerizeSeo = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  // Fetch data from API
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/reports/seo/report');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching SEO report data:', error);
      alert('Failed to load SEO report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  // Helper functions
  const getHealthScoreColor = (score) => {
    if (score >= 90) return '#22c55e';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const getSeverityBadge = (count, type = 'warning') => {
    const colors = {
      'critical': { bg: '#fef2f2', color: '#dc2626' },
      'warning': { bg: '#fef3c7', color: '#d97706' },
      'success': { bg: '#f0fdf4', color: '#15803d' },
      'info': { bg: '#eff6ff', color: '#2563eb' }
    };
    
    const colorScheme = colors[type] || colors.warning;
    
    return (
      <span style={{ 
        backgroundColor: colorScheme.bg, 
        color: colorScheme.color, 
        padding: '4px 12px', 
        borderRadius: '16px',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        {count}
      </span>
    );
  };

  // Export functionality
  const exportData = (format) => {
    alert(`Exporting SEO data as ${format.toUpperCase()}...`);
    // TODO: Implement actual export functionality
  };

  if (loading) {
    return (
      <AuthLayout>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          fontSize: '18px'
        }}>
          Loading SEO Report...
        </div>
      </AuthLayout>
    );
  }

  const resourceDistribution = data.resource_distribution || {};
  const primarySeoHealth = data.primary_seo_health || {};
  const metaFieldAnalysis = data.meta_field_analysis || {};
  const redirectHealth = data.redirect_health || {};
  const technicalIssues = data.technical_issues || {};
  const keywordAnalytics = data.keyword_analytics || {};
  const contentAnalysis = data.content_analysis || {};

  return (
    <AuthLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
              SEO Health Report
            </h1>
            <p style={{ color: '#666', margin: '0 0 16px 0', lineHeight: '1.5' }}>
              Comprehensive SEO analysis and optimization insights for your resource database
            </p>
            
            {/* Quick Guide */}
            <div style={{ 
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                📊 Quick Guide to Metrics:
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                <strong>Resource Distribution:</strong> Shows total resources and their status (primary = no canonical URL, related = has canonical URL) • 
                <strong>SEO Health:</strong> Percentage of resources with complete meta data (title + description + keywords) • 
                <strong>Content Quality:</strong> Analysis of resource names, descriptions, and URL slugs • 
                <strong>Keywords:</strong> Usage patterns and density across your resources
              </div>
            </div>

            {/* Overall Health Summary */}
            <div style={{ 
              backgroundColor: primarySeoHealth.completeness_percentage >= 80 ? '#f0fdf4' : 
                             primarySeoHealth.completeness_percentage >= 60 ? '#fefce8' : '#fef2f2',
              border: `1px solid ${primarySeoHealth.completeness_percentage >= 80 ? '#bbf7d0' : 
                                  primarySeoHealth.completeness_percentage >= 60 ? '#fde047' : '#fecaca'}`,
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '8px'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', 
                color: primarySeoHealth.completeness_percentage >= 80 ? '#166534' : 
                       primarySeoHealth.completeness_percentage >= 60 ? '#ca8a04' : '#dc2626',
                marginBottom: '4px' 
              }}>
                {primarySeoHealth.completeness_percentage >= 80 ? '🟢 SEO Health: Excellent' : 
                 primarySeoHealth.completeness_percentage >= 60 ? '🟡 SEO Health: Good' : '🔴 SEO Health: Needs Attention'}
              </div>
              <div style={{ fontSize: '13px', 
                color: primarySeoHealth.completeness_percentage >= 80 ? '#166534' : 
                       primarySeoHealth.completeness_percentage >= 60 ? '#ca8a04' : '#dc2626',
                lineHeight: '1.4' 
              }}>
                {primarySeoHealth.completeness_percentage >= 80 ? 
                  'Your SEO is well-optimized! Focus on maintaining quality and monitoring performance.' :
                 primarySeoHealth.completeness_percentage >= 60 ? 
                  'Good foundation, but there\'s room for improvement. Consider optimizing missing meta data.' :
                  'Significant SEO optimization needed. Priority: add meta titles, descriptions, and keywords.'}
              </div>
            </div>
            
            <div style={{ fontSize: '14px', color: '#888' }}>
              Generated at: {data.summary?.generated_at ? new Date(data.summary.generated_at).toLocaleString() : 'N/A'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={fetchReportData}
              style={{
                padding: '8px 16px',
                border: '1px solid #3b82f6',
                backgroundColor: 'transparent',
                color: '#3b82f6',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🔄 Refresh
            </button>
            <button
              onClick={() => exportData('csv')}
              style={{
                padding: '8px 16px',
                border: '1px solid #22c55e',
                backgroundColor: 'transparent',
                color: '#22c55e',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              📥 Export CSV
            </button>
          </div>
        </div>

        {/* Summary Cards Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '24px',
          marginBottom: '24px'
        }}>
          {/* Resource Distribution Widget */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0' }}>
                Resource Distribution
              </h3>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'help',
                border: '1px solid #e5e7eb'
              }} title="Primary resources are standalone pages (no canonical URL). Related resources point to other pages via canonical URL.">
                ℹ️ What's this?
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', lineHeight: '1.4' }}>
              Shows the breakdown of your resource database by status and SEO structure
            </div>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ 
                fontSize: '36px', 
                fontWeight: 'bold', 
                color: '#3b82f6',
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                {(resourceDistribution.total_resources || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '16px', color: '#666', fontWeight: '500' }}>Total Resources</div>
              
              {/* Total Active Resources Highlight */}
              <div style={{ 
                marginTop: '12px',
                padding: '8px 12px',
                backgroundColor: '#dbeafe',
                border: '2px solid #3b82f6',
                borderRadius: '8px',
                display: 'inline-block'
              }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1d4ed8' }}>
                  {(resourceDistribution.total_active_resources || 
                    ((resourceDistribution.active_primary_resources || 0) + (resourceDistribution.active_related_resources || 0))
                  ).toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: '#1e40af', fontWeight: '600' }}>
                  Total Active Resources ({resourceDistribution.active_percentage || 0}%)
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px' }}>
                <span style={{ fontWeight: '500' }}>
                  Active Primary:
                  <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>(standalone pages)</span>
                </span>
                <span style={{ 
                  fontWeight: '700', 
                  color: '#22c55e', 
                  fontSize: '16px',
                  padding: '4px 8px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '6px',
                  border: '1px solid #bbf7d0'
                }}>
                  {(resourceDistribution.active_primary_resources || 0).toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px' }}>
                <span style={{ fontWeight: '500' }}>
                  Active Related:
                  <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>(canonical links)</span>
                </span>
                <span style={{ 
                  fontWeight: '700', 
                  color: '#f59e0b', 
                  fontSize: '16px',
                  padding: '4px 8px',
                  backgroundColor: '#fffbeb',
                  borderRadius: '6px',
                  border: '1px solid #fed7aa'
                }}>
                  {(resourceDistribution.active_related_resources || 0).toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px' }}>
                <span style={{ fontWeight: '500' }}>
                  Deleted:
                  <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>(removed content)</span>
                </span>
                <span style={{ 
                  fontWeight: '700', 
                  color: '#ef4444', 
                  fontSize: '16px',
                  padding: '4px 8px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '6px',
                  border: '1px solid #fecaca'
                }}>
                  {(resourceDistribution.deleted_resources || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* SEO Completeness Widget */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0' }}>
                Primary SEO Health
              </h3>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'help',
                border: '1px solid #e5e7eb'
              }} title="Shows how many primary resources have complete SEO meta data (title + description + keywords). Higher percentage = better SEO coverage.">
                ℹ️ SEO Score
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', lineHeight: '1.4' }}>
              Percentage of primary resources with complete meta data (title, description, keywords)
            </div>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '4px',
                marginBottom: '8px'
              }}>
                <div style={{ 
                  width: `${primarySeoHealth.completeness_percentage || 0}%`, 
                  height: '100%', 
                  backgroundColor: getHealthScoreColor(primarySeoHealth.completeness_percentage || 0),
                  borderRadius: '4px'
                }}></div>
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {primarySeoHealth.completeness_percentage || 0}%
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>SEO Completeness</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Complete SEO:</span>
                {getSeverityBadge(primarySeoHealth.complete_seo || 0, 'success')}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Partial SEO:</span>
                {getSeverityBadge(primarySeoHealth.partial_seo || 0, 'warning')}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Missing SEO:</span>
                {getSeverityBadge(primarySeoHealth.missing_seo || 0, 'critical')}
              </div>
            </div>
          </div>

          {/* Redirect Health Widget */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#ffffff'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
              Redirect Health
            </h3>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>
                {redirectHealth.redirect_percentage || 0}%
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Deleted Resources with Redirects</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Total Deleted:</span>
                <span style={{ fontWeight: '600' }}>{redirectHealth.deleted_total || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>With Redirect:</span>
                {getSeverityBadge(redirectHealth.with_redirect || 0, 'success')}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Without Redirect:</span>
                {getSeverityBadge(redirectHealth.without_redirect || 0, 'critical')}
              </div>
            </div>
            {(redirectHealth.without_redirect || 0) > 0 && (
              <button style={{
                width: '100%',
                padding: '8px 16px',
                marginTop: '16px',
                border: '1px solid #dc2626',
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}>
                ⚠️ Fix Missing Redirects
              </button>
            )}
          </div>

          {/* Technical Issues Widget */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#ffffff'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
              Technical Issues
            </h3>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
                {(technicalIssues.duplicate_titles || 0) + (technicalIssues.duplicate_descriptions || 0) + (technicalIssues.missing_slugs || 0)}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Total Issues Found</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Duplicate Titles:</span>
                {getSeverityBadge(technicalIssues.duplicate_titles || 0, 'warning')}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Duplicate Descriptions:</span>
                {getSeverityBadge(technicalIssues.duplicate_descriptions || 0, 'warning')}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Missing Slugs:</span>
                {getSeverityBadge(technicalIssues.missing_slugs || 0, 'critical')}
              </div>
            </div>
          </div>

          {/* Keyword Analytics Widget */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0' }}>
                Keyword Analytics
              </h3>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'help',
                border: '1px solid #e5e7eb'
              }} title="Analyzes keyword usage patterns across resources. Higher coverage and density = better keyword optimization.">
                ℹ️ Keywords
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', lineHeight: '1.4' }}>
              Analysis of keyword usage patterns and density across your resource database
            </div>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#8b5cf6' }}>
                {keywordAnalytics.total_unique_keywords || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Unique Keywords</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>
                  Avg Keywords/Resource:
                  <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>(target: 3-5)</span>
                </span>
                <span style={{ fontWeight: '600', color: '#8b5cf6' }}>
                  {keywordAnalytics.density_metrics?.avg_keywords_per_resource || 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>
                  Keyword Coverage:
                  <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>(% with keywords)</span>
                </span>
                <span style={{ fontWeight: '600', color: '#22c55e' }}>
                  {keywordAnalytics.density_metrics?.keyword_coverage || 0}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>
                  Total Instances:
                  <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>(all keywords)</span>
                </span>
                <span style={{ fontWeight: '600', color: '#f59e0b' }}>
                  {keywordAnalytics.total_keyword_instances || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Content Analysis Widget */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0' }}>
                Content Quality
              </h3>
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                padding: '4px 8px',
                borderRadius: '4px',
                cursor: 'help',
                border: '1px solid #e5e7eb'
              }} title="Evaluates the quality of resource names, descriptions, and URL slugs. Higher scores indicate better on-page SEO structure.">
                ℹ️ Content
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', lineHeight: '1.4' }}>
              Quality assessment of resource names, descriptions, and URL structure for on-page SEO
            </div>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#06b6d4' }}>
                {contentAnalysis.name_analysis?.optimization_score || 0}%
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Name Quality Score</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Description Coverage:</span>
                <span style={{ fontWeight: '600', color: '#06b6d4' }}>
                  {contentAnalysis.description_analysis?.coverage_percentage || 0}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Slug Coverage:</span>
                <span style={{ fontWeight: '600', color: '#06b6d4' }}>
                  {contentAnalysis.slug_analysis?.coverage_percentage || 0}%
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>Content-Meta Alignment:</span>
                <span style={{ fontWeight: '600', color: '#22c55e' }}>
                  {contentAnalysis.content_meta_alignment?.title_alignment_percentage || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Analytics Tables */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '24px',
          marginBottom: '24px'
        }}>
          {/* Content Analysis Table */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ padding: '20px 20px 0 20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
                On-Page Content Analysis
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 16px 0', lineHeight: '1.4' }}>
                Quality assessment of core content fields that directly impact search engine rankings and user experience.
              </p>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      Field
                    </th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      Coverage
                    </th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      Avg Length
                    </th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      Quality
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 20px', fontSize: '14px', fontWeight: '600' }}>
                      <span style={{ color: '#06b6d4' }}>📝 Name</span>
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      100%
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      {contentAnalysis.name_analysis?.avg_length || 0} chars
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      <span style={{ 
                        backgroundColor: (contentAnalysis.name_analysis?.optimization_score || 0) >= 80 ? '#dcfce7' : 
                                       (contentAnalysis.name_analysis?.optimization_score || 0) >= 60 ? '#fef3c7' : '#fecaca',
                        color: (contentAnalysis.name_analysis?.optimization_score || 0) >= 80 ? '#166534' : 
                               (contentAnalysis.name_analysis?.optimization_score || 0) >= 60 ? '#92400e' : '#dc2626',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {contentAnalysis.name_analysis?.optimization_score || 0}%
                      </span>
                    </td>
                  </tr>
                  <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 20px', fontSize: '14px', fontWeight: '600' }}>
                      <span style={{ color: '#06b6d4' }}>📄 Description</span>
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      {contentAnalysis.description_analysis?.coverage_percentage || 0}%
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      {contentAnalysis.description_analysis?.avg_length || 0} chars
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      <span style={{ 
                        backgroundColor: (contentAnalysis.description_analysis?.coverage_percentage || 0) >= 80 ? '#dcfce7' : 
                                       (contentAnalysis.description_analysis?.coverage_percentage || 0) >= 60 ? '#fef3c7' : '#fecaca',
                        color: (contentAnalysis.description_analysis?.coverage_percentage || 0) >= 80 ? '#166534' : 
                               (contentAnalysis.description_analysis?.coverage_percentage || 0) >= 60 ? '#92400e' : '#dc2626',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {(contentAnalysis.description_analysis?.coverage_percentage || 0) >= 80 ? 'Good' : 
                         (contentAnalysis.description_analysis?.coverage_percentage || 0) >= 60 ? 'Fair' : 'Poor'}
                      </span>
                    </td>
                  </tr>
                  <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 20px', fontSize: '14px', fontWeight: '600' }}>
                      <span style={{ color: '#06b6d4' }}>🔗 Slug</span>
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      {contentAnalysis.slug_analysis?.coverage_percentage || 0}%
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      {contentAnalysis.slug_analysis?.avg_length || 0} chars
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px' }}>
                      <span style={{ 
                        backgroundColor: (contentAnalysis.slug_analysis?.coverage_percentage || 0) >= 95 ? '#dcfce7' : 
                                       (contentAnalysis.slug_analysis?.coverage_percentage || 0) >= 80 ? '#fef3c7' : '#fecaca',
                        color: (contentAnalysis.slug_analysis?.coverage_percentage || 0) >= 95 ? '#166534' : 
                               (contentAnalysis.slug_analysis?.coverage_percentage || 0) >= 80 ? '#92400e' : '#dc2626',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {(contentAnalysis.slug_analysis?.coverage_percentage || 0) >= 95 ? 'Excellent' : 
                         (contentAnalysis.slug_analysis?.coverage_percentage || 0) >= 80 ? 'Good' : 'Poor'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {/* Keyword Analytics Table */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ padding: '20px 20px 0 20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
                Top Keywords Usage
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 16px 0', lineHeight: '1.4' }}>
                Most frequently used keywords across your resources. High usage indicates popular themes and search targets.
              </p>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      Keyword
                    </th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      Usage Count
                    </th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                      Resources
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(keywordAnalytics.top_keywords || []).slice(0, 10).map((keyword, index) => (
                    <tr key={index} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ 
                        padding: '12px 20px', 
                        fontSize: '14px',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        <span style={{ 
                          backgroundColor: '#f3f4f6',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {keyword.keyword || 'N/A'}
                        </span>
                      </td>
                      <td style={{ 
                        padding: '12px 20px', 
                        textAlign: 'center', 
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#8b5cf6'
                      }}>
                        {keyword.usage_count || 0}
                      </td>
                      <td style={{ 
                        padding: '12px 20px', 
                        textAlign: 'center', 
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        {keyword.resource_count || 0}
                      </td>
                    </tr>
                  ))}
                  {(!keywordAnalytics.top_keywords || keywordAnalytics.top_keywords.length === 0) && (
                    <tr>
                      <td colSpan="3" style={{ 
                        padding: '24px 20px', 
                        textAlign: 'center', 
                        fontSize: '14px', 
                        color: '#9ca3af',
                        fontStyle: 'italic'
                      }}>
                        No keyword data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Meta Title Analysis Table */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ padding: '20px 20px 0 20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
                Meta Title Analysis
              </h3>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                      Status
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                      Count
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#f0fdf4',
                        color: '#15803d',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Present
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.title_analysis?.present || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.title_analysis?.present || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#f0fdf4',
                        color: '#15803d',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Optimal Length
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.title_analysis?.optimal_length || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.title_analysis?.optimal_length || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#fef3c7',
                        color: '#d97706',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Too Short
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.title_analysis?.too_short || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.title_analysis?.too_short || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Too Long
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.title_analysis?.too_long || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.title_analysis?.too_long || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Missing
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.title_analysis?.missing || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.title_analysis?.missing || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Meta Description Analysis Table */}
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: '#ffffff'
          }}>
            <div style={{ padding: '20px 20px 0 20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px 0' }}>
                Meta Description Analysis
              </h3>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                      Status
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                      Count
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#f0fdf4',
                        color: '#15803d',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Present
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.description_analysis?.present || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.description_analysis?.present || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#f0fdf4',
                        color: '#15803d',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Optimal Length
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.description_analysis?.optimal_length || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.description_analysis?.optimal_length || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#fef3c7',
                        color: '#d97706',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Too Short
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.description_analysis?.too_short || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.description_analysis?.too_short || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Too Long
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.description_analysis?.too_long || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.description_analysis?.too_long || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ 
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        padding: '4px 12px', 
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Missing
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {(metaFieldAnalysis.description_analysis?.missing || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                      {primarySeoHealth.total_primary > 0 ? 
                        Math.round((metaFieldAnalysis.description_analysis?.missing || 0) / primarySeoHealth.total_primary * 100) : 0
                      }%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* SEO Optimization Recommendations */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          backgroundColor: '#ffffff',
          marginBottom: '24px'
        }}>
          <div style={{ 
            padding: '20px', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: '1px solid #f3f4f6'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
              SEO Optimization Recommendations
            </h3>
            <button style={{
              padding: '6px 12px',
              border: '1px solid #3b82f6',
              backgroundColor: '#eff6ff',
              color: '#2563eb',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}>
              Generate SEO Report
            </button>
          </div>
          
          <div style={{ padding: '20px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              {/* Priority Actions */}
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fef2f2', 
                borderRadius: '8px',
                border: '1px solid #fecaca'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#dc2626' }}>
                  🚨 Critical Issues
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                  {(primarySeoHealth.missing_seo || 0) > 0 && (
                    <li>Fix {primarySeoHealth.missing_seo} resources with completely missing SEO</li>
                  )}
                  {(redirectHealth.without_redirect || 0) > 0 && (
                    <li>Add redirects for {redirectHealth.without_redirect} deleted resources</li>
                  )}
                  {(technicalIssues.missing_slugs || 0) > 0 && (
                    <li>Generate slugs for {technicalIssues.missing_slugs} resources</li>
                  )}
                </ul>
              </div>
              
              {/* Improvement Opportunities */}
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '8px',
                border: '1px solid #fed7aa'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#d97706' }}>
                  ⚡ Quick Wins
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                  {(primarySeoHealth.partial_seo || 0) > 0 && (
                    <li>Complete SEO for {primarySeoHealth.partial_seo} partially optimized resources</li>
                  )}
                  {(technicalIssues.duplicate_titles || 0) > 0 && (
                    <li>Fix {technicalIssues.duplicate_titles} duplicate meta titles</li>
                  )}
                  {(technicalIssues.duplicate_descriptions || 0) > 0 && (
                    <li>Fix {technicalIssues.duplicate_descriptions} duplicate descriptions</li>
                  )}
                </ul>
              </div>
              
              {/* Success Metrics */}
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0fdf4', 
                borderRadius: '8px',
                border: '1px solid #bbf7d0'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#15803d' }}>
                  ✅ Well Optimized
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                  <li>{primarySeoHealth.complete_seo || 0} resources with complete SEO</li>
                  <li>{redirectHealth.with_redirect || 0} deleted resources with proper redirects</li>
                  <li>{metaFieldAnalysis.keywords_analysis?.present || 0} resources with meta keywords</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Keywords Analysis */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}>
          <div style={{ 
            padding: '20px', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: '1px solid #f3f4f6'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
              Keywords Analysis
            </h3>
          </div>
          
          <div style={{ padding: '20px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ 
                textAlign: 'center', 
                padding: '16px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>
                  {metaFieldAnalysis.keywords_analysis?.present || 0}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>Resources with Keywords</div>
              </div>
              <div style={{ 
                textAlign: 'center', 
                padding: '16px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
                  {metaFieldAnalysis.keywords_analysis?.missing || 0}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>Missing Keywords</div>
              </div>
              <div style={{ 
                textAlign: 'center', 
                padding: '16px', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                  {metaFieldAnalysis.keywords_analysis?.avg_keyword_count || 0}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>Avg Keywords per Resource</div>
              </div>
            </div>
            
            {(metaFieldAnalysis.keywords_analysis?.missing || 0) > 0 && (
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '8px', 
                border: '1px solid #fed7aa',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
                  {metaFieldAnalysis.keywords_analysis?.missing || 0} resources need keyword optimization
                </p>
                <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#92400e' }}>
                  Current keyword coverage: {keywordAnalytics.density_metrics?.keyword_coverage || 0}% | 
                  Avg keywords per resource: {keywordAnalytics.density_metrics?.avg_keywords_per_resource || 0}
                </p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button style={{
                    padding: '8px 16px',
                    border: '1px solid #d97706',
                    backgroundColor: '#fff',
                    color: '#d97706',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                    🏷️ Bulk Generate Keywords
                  </button>
                  <button style={{
                    padding: '8px 16px',
                    border: '1px solid #8b5cf6',
                    backgroundColor: '#fff',
                    color: '#8b5cf6',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                    📊 Keyword Analysis Report
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Keyword Strategy Section */}
        {keywordAnalytics.top_keywords && keywordAnalytics.top_keywords.length > 0 && (
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '24px',
            backgroundColor: '#ffffff',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 20px 0', color: '#8b5cf6' }}>
              📊 Keyword Strategy Insights
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '20px' 
            }}>
              {/* Keyword Performance */}
              <div style={{ 
                padding: '16px',
                backgroundColor: '#faf5ff',
                borderRadius: '8px',
                border: '1px solid #e9d5ff'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#8b5cf6' }}>
                  🎯 Top Performing Keywords
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {keywordAnalytics.top_keywords.slice(0, 5).map((keyword, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      fontSize: '14px',
                      padding: '6px 0'
                    }}>
                      <span style={{ 
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {keyword.keyword}
                      </span>
                      <span style={{ fontWeight: '600', color: '#6b46c1' }}>
                        {keyword.usage_count} uses
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Keyword Density Analysis */}
              <div style={{ 
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#0369a1' }}>
                  📈 Density Analysis
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span>Keyword Coverage:</span>
                    <span style={{ fontWeight: '600', color: '#0369a1' }}>
                      {keywordAnalytics.density_metrics?.keyword_coverage || 0}%
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span>Avg Keywords/Resource:</span>
                    <span style={{ fontWeight: '600', color: '#0369a1' }}>
                      {keywordAnalytics.density_metrics?.avg_keywords_per_resource || 0}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span>Max Keywords:</span>
                    <span style={{ fontWeight: '600', color: '#0369a1' }}>
                      {keywordAnalytics.density_metrics?.max_keywords || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Keyword Recommendations */}
              <div style={{ 
                padding: '16px',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #bbf7d0'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#059669' }}>
                  💡 Keyword Recommendations
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                  {keywordAnalytics.density_metrics?.keyword_coverage < 50 && (
                    <div style={{ 
                      padding: '8px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '4px',
                      border: '1px solid #fed7aa'
                    }}>
                      ⚠️ Low keyword coverage - add keywords to more resources
                    </div>
                  )}
                  {keywordAnalytics.density_metrics?.avg_keywords_per_resource < 3 && (
                    <div style={{ 
                      padding: '8px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '4px',
                      border: '1px solid #fed7aa'
                    }}>
                      📝 Consider adding more keywords per resource (target: 3-5)
                    </div>
                  )}
                  {keywordAnalytics.total_unique_keywords > 100 && (
                    <div style={{ 
                      padding: '8px',
                      backgroundColor: '#dbeafe',
                      borderRadius: '4px',
                      border: '1px solid #93c5fd'
                    }}>
                      🎉 Great keyword diversity - {keywordAnalytics.total_unique_keywords} unique terms
                    </div>
                  )}
                  <button style={{
                    padding: '8px 12px',
                    border: '1px solid #059669',
                    backgroundColor: '#f0fdf4',
                    color: '#059669',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    marginTop: '8px'
                  }}>
                    🔍 Analyze Keyword Opportunities
                  </button>
                </div>
              </div>
              {/* Content-Meta Alignment */}
              <div style={{ 
                padding: '16px',
                backgroundColor: '#fefce8',
                borderRadius: '8px',
                border: '1px solid #fde047'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#ca8a04' }}>
                  🔗 Content-Meta Alignment
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Title-Name Match:</span>
                    <span style={{ fontWeight: '600', color: '#ca8a04' }}>
                      {contentAnalysis.content_meta_alignment?.title_alignment_percentage || 0}%
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Description Alignment:</span>
                    <span style={{ fontWeight: '600', color: '#ca8a04' }}>
                      {contentAnalysis.content_meta_alignment?.desc_alignment_percentage || 0}%
                    </span>
                  </div>
                  {(contentAnalysis.content_meta_alignment?.title_alignment_percentage || 0) < 50 && (
                    <div style={{ 
                      padding: '6px',
                      backgroundColor: '#fecaca',
                      borderRadius: '4px',
                      border: '1px solid #f87171',
                      fontSize: '12px'
                    }}>
                      ⚠️ Low alignment - meta titles should match resource names
                    </div>
                  )}
                  <button style={{
                    padding: '6px 10px',
                    border: '1px solid #ca8a04',
                    backgroundColor: '#fefce8',
                    color: '#ca8a04',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    marginTop: '6px'
                  }}>
                    🔄 Sync Content & Meta
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Quality Deep Dive */}
        {contentAnalysis.total_resources > 0 && (
          <div style={{ 
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '24px',
            backgroundColor: '#ffffff',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 20px 0', color: '#06b6d4' }}>
              📋 Content Quality Deep Dive
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
              gap: '20px' 
            }}>
              {/* Name Analysis */}
              <div style={{ 
                padding: '16px',
                backgroundColor: '#f0fdfa',
                borderRadius: '8px',
                border: '1px solid #7dd3fc'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#0e7490' }}>
                  📝 Resource Names Analysis
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Average Length:</span>
                    <span style={{ fontWeight: '600' }}>{contentAnalysis.name_analysis?.avg_length || 0} chars</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Average Words:</span>
                    <span style={{ fontWeight: '600' }}>{contentAnalysis.name_analysis?.avg_words || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Optimal Length:</span>
                    <span style={{ fontWeight: '600', color: '#059669' }}>{contentAnalysis.name_analysis?.optimal_length || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Too Short (&lt;10 chars):</span>
                    <span style={{ fontWeight: '600', color: '#dc2626' }}>{contentAnalysis.name_analysis?.too_short || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Too Long (&gt;100 chars):</span>
                    <span style={{ fontWeight: '600', color: '#d97706' }}>{contentAnalysis.name_analysis?.too_long || 0}</span>
                  </div>
                </div>
              </div>

              {/* Description Analysis */}
              <div style={{ 
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #93c5fd'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#1d4ed8' }}>
                  📄 Description Analysis
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>With Description:</span>
                    <span style={{ fontWeight: '600', color: '#1d4ed8' }}>{contentAnalysis.description_analysis?.with_description || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Coverage:</span>
                    <span style={{ fontWeight: '600' }}>{contentAnalysis.description_analysis?.coverage_percentage || 0}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Avg Length:</span>
                    <span style={{ fontWeight: '600' }}>{contentAnalysis.description_analysis?.avg_length || 0} chars</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Optimal (50-200):</span>
                    <span style={{ fontWeight: '600', color: '#059669' }}>{contentAnalysis.description_analysis?.optimal_length || 0}</span>
                  </div>
                  {(contentAnalysis.description_analysis?.coverage_percentage || 0) < 80 && (
                    <div style={{ 
                      padding: '6px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginTop: '4px'
                    }}>
                      💡 Consider adding descriptions to improve SEO
                    </div>
                  )}
                </div>
              </div>

              {/* Slug Analysis */}
              <div style={{ 
                padding: '16px',
                backgroundColor: '#fefce8',
                borderRadius: '8px',
                border: '1px solid #fde047'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#ca8a04' }}>
                  🔗 URL Slug Analysis  
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>With Slug:</span>
                    <span style={{ fontWeight: '600', color: '#ca8a04' }}>{contentAnalysis.slug_analysis?.with_slug || 0}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Coverage:</span>
                    <span style={{ fontWeight: '600' }}>{contentAnalysis.slug_analysis?.coverage_percentage || 0}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Avg Length:</span>
                    <span style={{ fontWeight: '600' }}>{contentAnalysis.slug_analysis?.avg_length || 0} chars</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Optimal (20-80):</span>
                    <span style={{ fontWeight: '600', color: '#059669' }}>{contentAnalysis.slug_analysis?.optimal_length || 0}</span>
                  </div>
                  {(contentAnalysis.slug_analysis?.coverage_percentage || 0) < 95 && (
                    <div style={{ 
                      padding: '6px',
                      backgroundColor: '#fecaca',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginTop: '4px'
                    }}>
                      ⚠️ Missing slugs impact URL structure and SEO
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content Optimization Recommendations */}
            <div style={{ 
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #cbd5e1'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px 0', color: '#475569' }}>
                🎯 Content Optimization Recommendations
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '12px',
                fontSize: '14px'
              }}>
                {(contentAnalysis.name_analysis?.optimization_score || 0) < 80 && (
                  <div style={{ 
                    padding: '8px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '4px',
                    border: '1px solid #fbbf24'
                  }}>
                    📝 Optimize {contentAnalysis.name_analysis?.too_short || 0} short names and {contentAnalysis.name_analysis?.too_long || 0} long names
                  </div>
                )}
                {(contentAnalysis.description_analysis?.coverage_percentage || 0) < 70 && (
                  <div style={{ 
                    padding: '8px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '4px',
                    border: '1px solid #60a5fa'
                  }}>
                    📄 Add descriptions to {contentAnalysis.total_resources - (contentAnalysis.description_analysis?.with_description || 0)} resources
                  </div>
                )}
                {(contentAnalysis.slug_analysis?.coverage_percentage || 0) < 95 && (
                  <div style={{ 
                    padding: '8px',
                    backgroundColor: '#fecaca',
                    borderRadius: '4px',
                    border: '1px solid #f87171'
                  }}>
                    🔗 Generate slugs for {contentAnalysis.total_resources - (contentAnalysis.slug_analysis?.with_slug || 0)} resources
                  </div>
                )}
                {(contentAnalysis.content_meta_alignment?.title_alignment_percentage || 0) < 60 && (
                  <div style={{ 
                    padding: '8px',
                    backgroundColor: '#f3e8ff',
                    borderRadius: '4px',
                    border: '1px solid #c084fc'
                  }}>
                    🔄 Improve content-meta alignment for better SEO consistency
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Metrics Explanation Guide */}
        <div style={{ 
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '24px',
          backgroundColor: '#f8fafc',
          marginBottom: '24px'
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 20px 0', color: '#374151' }}>
            📚 Understanding Your SEO Metrics
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '20px' 
          }}>
            {/* Resource Distribution Explanation */}
            <div style={{ 
              padding: '16px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#3b82f6' }}>
                🗂️ Resource Distribution
              </h4>
              <ul style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', paddingLeft: '16px', margin: '0' }}>
                <li><strong>Primary Resources:</strong> Standalone pages without canonical URLs - these are your main SEO targets</li>
                <li><strong>Related Resources:</strong> Pages that point to other resources via canonical URLs - consolidate SEO authority</li>
                <li><strong>Active %:</strong> Higher percentage means more resources are live and contributing to SEO</li>
              </ul>
            </div>

            {/* SEO Health Explanation */}
            <div style={{ 
              padding: '16px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#22c55e' }}>
                ❤️ SEO Health Score
              </h4>
              <ul style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', paddingLeft: '16px', margin: '0' }}>
                <li><strong>Complete SEO:</strong> Resources with title + description + keywords (ideal: 80%+)</li>
                <li><strong>Partial SEO:</strong> Resources missing 1-2 meta fields (needs improvement)</li>
                <li><strong>Missing SEO:</strong> Resources with no meta data (priority for optimization)</li>
              </ul>
            </div>

            {/* Content Quality Explanation */}
            <div style={{ 
              padding: '16px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#06b6d4' }}>
                📝 Content Quality
              </h4>
              <ul style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', paddingLeft: '16px', margin: '0' }}>
                <li><strong>Name Quality:</strong> Optimal length 10-100 chars for better readability and SEO</li>
                <li><strong>Description Coverage:</strong> Aim for 70%+ coverage with 50-200 char descriptions</li>
                <li><strong>Slug Coverage:</strong> Should be 95%+ for proper URL structure</li>
              </ul>
            </div>

            {/* Keyword Analytics Explanation */}
            <div style={{ 
              padding: '16px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#8b5cf6' }}>
                🏷️ Keyword Analytics
              </h4>
              <ul style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', paddingLeft: '16px', margin: '0' }}>
                <li><strong>Keyword Coverage:</strong> % of resources with keywords (target: 80%+)</li>
                <li><strong>Avg Keywords/Resource:</strong> Ideal range: 3-5 keywords per resource</li>
                <li><strong>Top Keywords:</strong> Most used terms - indicates content themes and search targets</li>
              </ul>
            </div>

            {/* Technical Issues Explanation */}
            <div style={{ 
              padding: '16px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#f59e0b' }}>
                ⚠️ Technical Issues
              </h4>
              <ul style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', paddingLeft: '16px', margin: '0' }}>
                <li><strong>Duplicate Titles:</strong> Same title on multiple resources - hurts SEO ranking</li>
                <li><strong>Duplicate Descriptions:</strong> Same description - reduces content uniqueness</li>
                <li><strong>Missing Slugs:</strong> Missing URL slugs - impacts site structure and crawling</li>
              </ul>
            </div>

            {/* Action Priority Guide */}
            <div style={{ 
              padding: '16px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#dc2626' }}>
                🎯 Priority Actions
              </h4>
              <ol style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5', paddingLeft: '16px', margin: '0' }}>
                <li>Fix missing slugs (critical for URL structure)</li>
                <li>Add meta data to resources with missing SEO</li>
                <li>Resolve duplicate titles and descriptions</li>
                <li>Improve keyword coverage and density</li>
                <li>Optimize content length and quality</li>
              </ol>
            </div>
          </div>

          {/* Score Interpretation */}
          <div style={{ 
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#f0f9ff',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
          }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0', color: '#0369a1' }}>
              📊 Score Interpretation Guide
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '12px',
              fontSize: '14px'
            }}>
              <div>
                <span style={{ color: '#22c55e', fontWeight: '600' }}>🟢 Excellent (90%+):</span> 
                <span style={{ color: '#4b5563' }}> Well optimized, minimal action needed</span>
              </div>
              <div>
                <span style={{ color: '#3b82f6', fontWeight: '600' }}>🔵 Good (70-89%):</span> 
                <span style={{ color: '#4b5563' }}> Solid foundation, minor improvements</span>
              </div>
              <div>
                <span style={{ color: '#f59e0b', fontWeight: '600' }}>🟡 Fair (50-69%):</span> 
                <span style={{ color: '#4b5563' }}> Needs attention, moderate priority</span>
              </div>
              <div>
                <span style={{ color: '#ef4444', fontWeight: '600' }}>🔴 Poor (&lt;50%):</span> 
                <span style={{ color: '#4b5563' }}> Requires immediate optimization</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ReportSummerizeSeo;